import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LatestHomeworkSummary } from '@family/shared';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export interface HomeworkProgressionPoint {
  timestamp: string;
  completionPercentage: number;
  completionText: string;
}

export interface HomeworkProgressionData {
  assignmentId: string;
  childName: string;
  subject: string;
  dueDate: string;
  firstSeenAt: string;
  completedAt: string | null;
  progression: HomeworkProgressionPoint[];
  previousAssignmentId: string | null;
  nextAssignmentId: string | null;
}

interface HomeworkResponse {
  data: LatestHomeworkSummary[];
  meta: {
    lastSyncedAt: string | null;
    lastSyncStatus: 'success' | 'error' | 'running' | 'idle';
    lastSyncError?: string;
    lastSyncHomeworkCount?: number;
  };
}

interface SyncResponse {
  message: string;
  data: { triggered: boolean };
}

/**
 * Fetches latest homework for all children
 */
export function useHomework() {
  return useQuery<HomeworkResponse>({
    queryKey: ['homework'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/homework`);
      if (!response.ok) {
        throw new Error('Failed to fetch homework');
      }
      return response.json();
    },
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
  });
}

/**
 * Triggers a manual homework sync for a specific child
 */
export function useSyncChild() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (childName: string) => {
      const response = await fetch(`${API_URL}/homework/sync/${childName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to trigger sync for ${childName}`);
      }

      return response.json() as Promise<SyncResponse>;
    },
    onSuccess: () => {
      // Wait a bit for the sync to complete, then refetch
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['homework'] });
      }, 3000);
    },
  });
}

/**
 * Fetches progression history for a homework assignment
 */
export function useHomeworkProgression(assignmentId: string | undefined) {
  return useQuery<HomeworkProgressionData>({
    queryKey: ['homework', assignmentId, 'progression'],
    queryFn: async () => {
      if (!assignmentId) {
        throw new Error('Assignment ID is required');
      }

      const response = await fetch(`${API_URL}/homework/${assignmentId}/progression`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch homework progression');
      }

      const json = await response.json();
      return json.data;
    },
    enabled: !!assignmentId,
  });
}
